import { getPurposesQueryOptions } from 'hooks/queries/getPurposesQueryOptions'
import { getPurposesUsQueryOptions } from 'hooks/queries/getPurposesUsQueryOptions'
import { enrichHomePrefetchQueryOptions, PrefetchableQueryOptions } from 'common/query-policy'
import { getFaqsQueryOptions } from 'pages/frequent-questions/hooks/queries/getFaqsQueryOptions'
import { getServicesQueryOptions } from 'pages/my-services/repository/queries/getServicesQueryOptions'
import { getRequestTermsQueryOptions } from 'pages/privacy-policy/hooks/queries/getRequestTermsQueryOptions'
import { getRequestTermsBody } from 'pages/privacy-policy/utils'
import { getReportStatusQueryOptions } from 'pages/your-personal-data/hooks/queries/getReportStatusQueryOptions'
import { getReportsHistoryQueryOptions } from 'pages/your-personal-data/hooks/queries/getReportsHistoryQueryOptions'
import { getReportsUnderAnalysisQueryOptions } from 'pages/your-personal-data/hooks/queries/getReportsUnderAnalysisQueryOptions'
import {
  TCountry,
  TEntity,
  TLanguage,
  TPrivacyCenterContent,
  TUserType,
} from 'contexts/app-context/types'

import { HomeFeatureFlags } from './deriveHomeFeatureFlags'
import { getActiveNotificationProductIds } from './getActiveNotificationProductIds'
import {
  buildFaqSearchPrefetchRequestDescriptor,
  buildPurposesPrefetchRequestDescriptor,
  buildPurposesUsPrefetchRequestDescriptor,
  buildReportStatusPrefetchRequestDescriptor,
  buildReportsPrefetchRequestDescriptor,
  buildRequestTermsPrefetchRequestDescriptor,
  buildServicesPrefetchRequestDescriptor,
} from './buildHomePrefetchRequestDescriptor'

interface CollectHomePrefetchQueryOptionsParams {
  privacyCenterContent: TPrivacyCenterContent
  userCountry: TCountry
  userSelectedEntity: TEntity
  userAccountType: TUserType
  userLanguage: TLanguage
  userEntities?: TEntity[]
  isUSBranch: boolean
  featureFlags: HomeFeatureFlags
}

const appendNotificationsPrefetch = (
  queryOptions: PrefetchableQueryOptions[],
  params: CollectHomePrefetchQueryOptionsParams,
) => {
  if (!params.featureFlags.shouldPrefetchNotifications) return

  const { endpoints } = params.privacyCenterContent

  if (params.isUSBranch) {
    const productMenu =
      params.privacyCenterContent.components.manageNotifications?.content.productsSection
        .productMenu

    getActiveNotificationProductIds(params.isUSBranch, productMenu).forEach((productId) => {
      queryOptions.push(
        enrichHomePrefetchQueryOptions(
          getPurposesUsQueryOptions({
            endpoint: endpoints.preferences?.getPreferencesUs,
            userCountry: params.userCountry,
            userSelectedEntity: params.userSelectedEntity,
            userAccountType: params.userAccountType,
            productId,
            isUSBranch: params.isUSBranch,
          }),
          buildPurposesUsPrefetchRequestDescriptor(
            endpoints.preferences?.getPreferencesUs,
            params.userCountry,
            params.userSelectedEntity,
            params.userAccountType,
            productId,
          ),
        ),
      )
    })

    return
  }

  queryOptions.push(
    enrichHomePrefetchQueryOptions(
      getPurposesQueryOptions({
        endpoint: endpoints.preferences?.getPreferences,
        userCountry: params.userCountry,
        userSelectedEntity: params.userSelectedEntity,
        userAccountType: params.userAccountType,
        isUSBranch: params.isUSBranch,
      }),
      buildPurposesPrefetchRequestDescriptor(
        endpoints.preferences?.getPreferences,
        params.userCountry,
        params.userSelectedEntity,
      ),
    ),
  )
}

const appendPersonalDataPrefetch = (
  queryOptions: PrefetchableQueryOptions[],
  params: CollectHomePrefetchQueryOptionsParams,
) => {
  if (!params.featureFlags.shouldPrefetchPersonalData) return

  const { endpoints } = params.privacyCenterContent
  const reportCards =
    params.privacyCenterContent.components.yourPersonalData?.content.reportCards ?? []

  reportCards
    .filter((reportCard) => reportCard.isActive)
    .forEach((reportCard) => {
      queryOptions.push(
        enrichHomePrefetchQueryOptions(
          getReportStatusQueryOptions({
            endpoint: endpoints.reports?.getReportsStatus,
            reportType: reportCard.type,
            userCountry: params.userCountry,
            userSelectedEntity: params.userSelectedEntity,
            isUSBranch: params.isUSBranch,
          }),
          buildReportStatusPrefetchRequestDescriptor(
            endpoints.reports?.getReportsStatus,
            reportCard.type,
            params.userCountry,
            params.userSelectedEntity,
          ),
        ),
      )
    })

  queryOptions.push(
    enrichHomePrefetchQueryOptions(
      getReportsHistoryQueryOptions({
        endpoint: endpoints.reports?.getReportsHistory,
        userCountry: params.userCountry,
        userSelectedEntity: params.userSelectedEntity,
        isUSBranch: params.isUSBranch,
      }),
      buildReportsPrefetchRequestDescriptor(
        endpoints.reports?.getReportsHistory,
        params.userCountry,
        params.userSelectedEntity,
      ),
    ),
  )

  queryOptions.push(
    enrichHomePrefetchQueryOptions(
      getReportsUnderAnalysisQueryOptions({
        endpoint: endpoints.reports?.getReportsInAnalysis,
        userCountry: params.userCountry,
        userSelectedEntity: params.userSelectedEntity,
        isUSBranch: params.isUSBranch,
      }),
      buildReportsPrefetchRequestDescriptor(
        endpoints.reports?.getReportsInAnalysis,
        params.userCountry,
        params.userSelectedEntity,
      ),
    ),
  )
}

const appendPrivacyPolicyPrefetch = (
  queryOptions: PrefetchableQueryOptions[],
  params: CollectHomePrefetchQueryOptionsParams,
) => {
  if (!params.featureFlags.shouldPrefetchPrivacyPolicy || !params.userEntities) return

  const requestBody = getRequestTermsBody({
    org: params.userCountry,
    userSelectedEntity: params.userSelectedEntity,
    userEntities: params.userEntities,
  })

  queryOptions.push(
    enrichHomePrefetchQueryOptions(
      getRequestTermsQueryOptions({
        endpoint: params.privacyCenterContent.endpoints.privacyPolicy?.requestPrivacyPolicies,
        requestBody,
      }),
      buildRequestTermsPrefetchRequestDescriptor(
        params.privacyCenterContent.endpoints.privacyPolicy?.requestPrivacyPolicies,
        requestBody,
      ),
    ),
  )
}

const appendFaqPrefetch = (
  queryOptions: PrefetchableQueryOptions[],
  params: CollectHomePrefetchQueryOptionsParams,
) => {
  if (!params.featureFlags.shouldPrefetchFaq) return

  queryOptions.push(
    enrichHomePrefetchQueryOptions(
      getFaqsQueryOptions({
        queryText: '',
        language: params.userLanguage,
        userSelectedEntity: params.userSelectedEntity,
      }),
      buildFaqSearchPrefetchRequestDescriptor('', params.userLanguage, params.userSelectedEntity),
    ),
  )
}

const appendServicesPrefetch = (
  queryOptions: PrefetchableQueryOptions[],
  params: CollectHomePrefetchQueryOptionsParams,
) => {
  if (!params.featureFlags.shouldPrefetchServices) return

  queryOptions.push(
    enrichHomePrefetchQueryOptions(
      getServicesQueryOptions({
        endpoint: params.privacyCenterContent.endpoints.services?.getServices,
        userCountry: params.userCountry,
        userSelectedEntity: params.userSelectedEntity,
        isUSBranch: params.isUSBranch,
      }),
      buildServicesPrefetchRequestDescriptor(
        params.privacyCenterContent.endpoints.services?.getServices,
        params.userCountry,
        params.userSelectedEntity,
      ),
    ),
  )
}

const collectHomePrefetchQueryOptions = (params: CollectHomePrefetchQueryOptionsParams) => {
  const queryOptions: PrefetchableQueryOptions[] = []

  appendNotificationsPrefetch(queryOptions, params)
  appendPersonalDataPrefetch(queryOptions, params)
  appendPrivacyPolicyPrefetch(queryOptions, params)
  appendFaqPrefetch(queryOptions, params)
  appendServicesPrefetch(queryOptions, params)

  return queryOptions
}

export type { CollectHomePrefetchQueryOptionsParams }
export { collectHomePrefetchQueryOptions }
